import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useParams } from 'react-router-dom'

import { useDocumentStore, useSearchDocuments } from '@/features/document'
import { useDebounce } from '@/hooks/useDebounce'
import { AISearchResultResponse } from '@/schema'
import { SEARCH_DEBOUNCE_DELAY_MS } from '@/utils/constants'

const MIN_SEARCH_LENGTH = 1

type UseTFLSidebarSearchProps = {
  searchText: string
}

export type UseTFLSidebarSearchReturn = {
  results: AISearchResultResponse[]
  loadMoreRef: React.RefObject<HTMLDivElement>
  isLoading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  refetch: () => void
  error: Error | null
  searchQuery: string
}

/**
 * Hook for managing TFL sidebar search functionality with performance optimizations
 */
export const useTFLSidebarSearch = (
  props: UseTFLSidebarSearchProps
): UseTFLSidebarSearchReturn => {
  const { searchText } = props

  const { id: deliverableSetId } = useParams<{ id: string }>()
  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  // Delays API request by SEARCH_DEBOUNCE_DELAY_MS ms after user stops typing
  const debouncedSearchText = useDebounce(searchText, SEARCH_DEBOUNCE_DELAY_MS)

  // Only recomputes when debouncedSearchText changes, not on every render
  const searchQuery = useMemo(() => {
    const trimmed = debouncedSearchText?.trim() || ''
    return trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : ''
  }, [debouncedSearchText])

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
    error,
  } = useSearchDocuments({
    deliverableSetId: deliverableSetId ?? '',
    searchQuery,
  })
  const setSearchText = useDocumentStore((state) => state.setSearchText)
  const setIsSearchResultsExists = useDocumentStore(
    (state) => state.setIsSearchResultsExists
  )

  const stateRef = useRef({ hasNextPage, isFetchingNextPage })

  useEffect(() => {
    setSearchText(searchQuery)
  }, [searchQuery, setSearchText])

  useEffect(() => {
    stateRef.current = { hasNextPage, isFetchingNextPage }
  }, [hasNextPage, isFetchingNextPage])

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0]
      if (
        entry?.isIntersecting &&
        stateRef.current.hasNextPage &&
        !stateRef.current.isFetchingNextPage
      ) {
        fetchNextPage()
      }
    },
    [fetchNextPage]
  )

  useEffect(() => {
    if (!searchQuery) return

    let observer: IntersectionObserver | null = null
    const timeoutId = setTimeout(() => {
      const targetElement = loadMoreRef.current
      if (!targetElement) return

      observer = new IntersectionObserver(handleIntersection, {
        root: null,
        rootMargin: '100px',
        threshold: 0.1,
      })

      observer.observe(targetElement)
    }, 0)

    return () => {
      clearTimeout(timeoutId)
      if (observer) {
        observer.disconnect()
      }
    }
  }, [handleIntersection, searchQuery])

  const results = useMemo(() => {
    return data?.pages?.flatMap((page) => page?.data || []) || []
  }, [data])

  const isSearchResultsExists = useMemo(
    () => !!results.length,
    [results.length]
  )

  useEffect(() => {
    setIsSearchResultsExists(isSearchResultsExists)
  }, [isSearchResultsExists, setIsSearchResultsExists])

  return useMemo(() => {
    return {
      results,
      loadMoreRef,
      isLoading,
      isFetchingNextPage,
      hasNextPage,
      refetch,
      error,
      searchQuery,
    }
  }, [
    results,
    loadMoreRef,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    error,
    searchQuery,
  ])
}
